import React, { useEffect } from 'react';
import { Button, Card, Col, Form, Input, Row, Select, Space } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import StoreSettingService from 'services/store_setting';
import { useApp } from 'contexts/app';

const service = new StoreSettingService();

const BrandProfile = () => {
  const [form] = Form.useForm();
  const { showLoading, showNotify, message } = useApp();

  const loadData = async () => {
    showLoading(true);
    try {
      const data = await service.getSEOBrandProfile();
      form.setFieldsValue({
        ...data,
        primary_keywords: Array.isArray(data?.primary_keywords) ? data.primary_keywords : [],
        target_locations: Array.isArray(data?.target_locations) ? data.target_locations : [],
        target_industries: Array.isArray(data?.target_industries) ? data.target_industries : [],
      });
    } catch (error) {
      message.error(error?.message || 'Không thể tải hồ sơ SEO thương hiệu');
    }
    showLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const fillMissingProfileFields = ({ current = {}, generated = {} }) => {
    const output = { ...(current || {}) };

    const fillTextKeys = ['slogan', 'value_proposition', 'tone_of_voice', 'full_description'];
    for (let index = 0; index < fillTextKeys.length; index++) {
      const key = fillTextKeys[index];
      const currentValue = String(output?.[key] || '').trim();
      const generatedValue = String(generated?.[key] || '').trim();
      if (!currentValue && generatedValue) {
        output[key] = generatedValue;
      }
    }

    const fillArrayKeys = ['primary_keywords', 'target_locations', 'target_industries'];
    for (let index = 0; index < fillArrayKeys.length; index++) {
      const key = fillArrayKeys[index];
      const currentList = Array.isArray(output?.[key]) ? output[key].filter(Boolean) : [];
      const generatedList = Array.isArray(generated?.[key]) ? generated[key].filter(Boolean) : [];
      if (currentList.length === 0 && generatedList.length > 0) {
        output[key] = generatedList;
      }
    }

    return output;
  };

  const handleGenerateByAI = async () => {
    const current = form.getFieldsValue(true);
    showLoading(true);
    try {
      const result = await service.generateSEOBrandProfileAI({
        brand_name: current?.brand_name,
        short_description: current?.short_description,
      });

      const next = fillMissingProfileFields({
        current,
        generated: result?.generated || {},
      });
      form.setFieldsValue(next);
      showNotify('Đã điền tự động các trường còn thiếu bằng AI');
    } catch (error) {
      message.error(error?.message || 'Không thể tạo thông tin bằng AI');
    }
    showLoading(false);
  };

  const onFinish = async (values) => {
    showLoading(true);
    try {
      await service.saveSEOBrandProfile(values);
      showNotify('Đã lưu hồ sơ SEO thương hiệu');
      await loadData();
    } catch (error) {
      message.error(error?.message || 'Không thể lưu hồ sơ SEO thương hiệu');
    }
    showLoading(false);
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="Hồ sơ SEO thương hiệu">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            primary_keywords: [],
            target_locations: [],
            target_industries: [],
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Tên thương hiệu" name="brand_name" rules={[{ required: true, message: 'Vui lòng nhập tên thương hiệu' }]}>
                <Input placeholder="Ví dụ: Thủy Tiên Food" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Slogan" name="slogan">
                <Input placeholder="Slogan thương hiệu" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Mô tả ngắn" name="short_description">
            <Input.TextArea rows={2} placeholder="Mô tả ngắn dùng cho snippet SEO" />
          </Form.Item>

          <Form.Item label="Mô tả đầy đủ" name="full_description">
            <Input.TextArea rows={5} placeholder="Mô tả đầy đủ bối cảnh thương hiệu" />
          </Form.Item>

          <Form.Item label="Giá trị cốt lõi (USP)" name="value_proposition">
            <Input.TextArea rows={2} placeholder="Điểm khác biệt chính của thương hiệu" />
          </Form.Item>

          <Form.Item>
            <Button icon={<RobotOutlined />} onClick={handleGenerateByAI}>
              Tạo thông tin còn thiếu bằng AI
            </Button>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Từ khóa lõi" name="primary_keywords">
                <Select mode="tags" tokenSeparators={[',']} placeholder="Nhập từ khóa rồi Enter" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Khu vực mục tiêu" name="target_locations">
                <Select mode="tags" tokenSeparators={[',']} placeholder="Ví dụ: TP.HCM, Hà Nội" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Ngành hàng mục tiêu" name="target_industries">
                <Select mode="tags" tokenSeparators={[',']} placeholder="Ví dụ: thực phẩm sỉ" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Tone of voice" name="tone_of_voice">
            <Input placeholder="Ví dụ: Chuyên nghiệp, gần gũi" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">Lưu hồ sơ SEO</Button>
          </Form.Item>
        </Form>
      </Card>
    </Space>
  );
};

export default BrandProfile;
